import * as Monitaro from '@/infrastructure/network/Monitaro/schema/models'
import { APIRequest } from '@/infrastructure/network/APIRequest'
import { HTTPMethod } from '@/infrastructure/network/types'

/**
 * ユーザー登録
 *
 * メールアドレスとパスワードを受け付けて、4桁のコードを生成してメールを送信する。
アクティベーションしていないメールアドレスの場合は再度コードを生成してアップデート
 */
export class PostSignup implements APIRequest<Monitaro.SignupResponse> {
  response: Monitaro.SignupResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.SignupRequest
  constructor(params: Monitaro.SignupRequest) {
    this.params = params
    this.path = `/signup`
  }
}

/**
 * ログイン
 *
 * Monitaro for hospitalのログイン機能
 */
export class PostLogin implements APIRequest<Monitaro.LoginResponse> {
  response: Monitaro.LoginResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.LoginRequest
  constructor(params: Monitaro.LoginRequest) {
    this.params = params
    this.path = `/login`
  }
}

/**
 * リフレッシュ
 *
 * Monitaro for hospitalのリフレッシュ機能
ログインが失敗したときに2週間以内にコールすると新しいトークンが返ってくる
 */
export class GetRefresh implements APIRequest<Monitaro.RefreshResponse> {
  response: Monitaro.RefreshResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.GET
  constructor() {
    this.path = `/refresh`
  }
}

/**
 * メールアドレスの変更
 *
 * メールアドレスの変更依頼で送られてきたidとcodeを使ってメールアドレスの変更を行う。
 */
export class PutChangeEmail implements APIRequest<Monitaro.ChangeEmailResponse> {
  response: Monitaro.ChangeEmailResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.PUT
  params: Monitaro.ChangeEmailRequest
  constructor(id: string, params: Monitaro.ChangeEmailRequest) {
    this.params = params
    this.path = `/change/email/${id}`
  }
}

/**
 * パスワードの変更依頼
 *
 * ログインしていない状態でパスワードの変更依頼メールを送信する。
 */
export class PostForget implements APIRequest<Monitaro.ForgetResponse> {
  response: Monitaro.ForgetResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.ForgetRequest
  constructor(params: Monitaro.ForgetRequest) {
    this.params = params
    this.path = `/forget`
  }
}

/**
 * パスワードの変更
 *
 * パスワード変更依頼で送られてきたidとcodeを使ってパスワードの変更を行う。
 */
export class PutChangePassword implements APIRequest<Monitaro.ChangePasswordResponse> {
  response: Monitaro.ChangePasswordResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.PUT
  params: Monitaro.ChangePasswordRequest
  constructor(id: string, params: Monitaro.ChangePasswordRequest) {
    this.params = params
    this.path = `/change/password/${id}`
  }
}

/**
 * ユーザーのメールアドレス認証
 *
 * idとcodeを指定して、ユーザー名とパスワードの設定が行える。
 */
export class PostIdentification implements APIRequest<Monitaro.IdentificationResponse> {
  response: Monitaro.IdentificationResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.IdentificationRequest
  constructor(user: string, params: Monitaro.IdentificationRequest) {
    this.params = params
    this.path = `/identification/${user}`
  }
}

/**
 * ログアウト
 *
 * Monitaro for hospitalのログアウト機能
ログインしていない状態でコールするとエラーを返す
 */
export class PostLogout implements APIRequest<Monitaro.LogoutResponse> {
  response: Monitaro.LogoutResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  constructor() {
    this.path = `/logout`
  }
}

/**
 * ログインユーザー情報
 *
 * ログインしているユーザーのユーザー情報を取得する
 */
export class GetMe implements APIRequest<Monitaro.MeResponse> {
  response: Monitaro.MeResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.GET
  constructor() {
    this.path = `/me`
  }
}

/**
 * ログインユーザー情報の更新
 *
 * ログインしているユーザーのユーザー情報を更新する
usernameとpasswordは変更することができない
 */
export class PutProfile implements APIRequest<Monitaro.ProfileResponse> {
  response: Monitaro.ProfileResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.PUT
  params: Monitaro.ProfileRequest
  constructor(params: Monitaro.ProfileRequest) {
    this.params = params
    this.path = `/profile`
  }
}

/**
 * FCMトークンの更新
 *
 * ログインしているユーザーのFCMトークンを更新する
 */
export class PutFcmToken implements APIRequest<Monitaro.TokenResponse> {
  response: Monitaro.TokenResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.PUT
  params: Monitaro.TokenRequest
  constructor(params: Monitaro.TokenRequest) {
    this.params = params
    this.path = `/profile/fcm`
  }
}

/**
 * ログインユーザーのメディア情報の更新
 *
 * ログインしているユーザーの画像データなどを更新する
 */
export class PutProfileMedia implements APIRequest<Monitaro.ProfileMediaResponse> {
  response: Monitaro.ProfileMediaResponse
  path: string
  contentType = 'multipart/form-data'
  method = HTTPMethod.PUT
  params: Monitaro.ProfileMediaRequest
  constructor(params: Monitaro.ProfileMediaRequest) {
    this.params = params
    this.path = `/profile/media`
  }
}

/**
 * MRユーザーのアクティベーション
 *
 * ユーザーIDを渡すことで利用開始できるようになる
 */
export class PostActivate implements APIRequest<Monitaro.ActivateResponse> {
  response: Monitaro.ActivateResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  constructor() {
    this.path = `/activate`
  }
}

/**
 * メールアドレスの変更依頼
 *
 * ログイン状態でメールドレスの変更通知を行う。
APIをコールすると変更用のメールが送信される。
 */
export class PostEmail implements APIRequest<Monitaro.EmailResponse> {
  response: Monitaro.EmailResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.EmailRequest
  constructor(params: Monitaro.EmailRequest) {
    this.params = params
    this.path = `/email`
  }
}

/**
 * パスワードの変更
 *
 * ログイン状態でパスワードの変更を行う。
 */
export class PostPassword implements APIRequest<Monitaro.PasswordResponse> {
  response: Monitaro.PasswordResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.PasswordRequest
  constructor(params: Monitaro.PasswordRequest) {
    this.params = params
    this.path = `/password`
  }
}

/**
 * 退会準備処理
 *
 * 退会準備フラグをオンにして、支払い以外できない状態にする
 */
export class PostWithdraw implements APIRequest<Monitaro.WithdrawResponse> {
  response: Monitaro.WithdrawResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  params: Monitaro.WithdrawRequest
  constructor(exec: boolean) {
    this.params = { exec }
    this.path = `/withdraw`
  }
}

/**
 * 退会処理
 *
 * 退会処理実施し、退会メールを送る
 */
export class PostRemove implements APIRequest<Monitaro.RemoveResponse> {
  response: Monitaro.RemoveResponse
  path: string
  contentType = 'application/json'
  method = HTTPMethod.POST
  constructor() {
    this.path = `/remove`
  }
}
